import React from 'react'

import * as Yup from 'yup'
import { BasicForm } from '../../../../core/components/form'
import { usePutForm, useFetch } from '../../../../hooks/manager/vendor'
import { useCacheCountry, getCountriesForSelect } from '../../../../hooks/cache/country'
import { useHistory, useParams } from 'react-router-dom'
import Loader from '../../../../components/loader'
import { isNotValid, translateSymfonyErrorToFormik } from '../../../../core/utils/form'

import { Form, Select, Input, Checkbox } from 'formik-antd'
import { Form as FormAntd, Divider } from 'antd'
import { selectTimezones } from '../../../../core/utils/timezones'
import { WorkingHours } from '@pointdevel/rc-working-hours'
import FormPrices from '../../../../components/price/form'
import FormServices from '../../../../components/service/form'

import { v4 } from 'uuid'

const _filter = (input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0

const ValidationSchema = Yup.object().shape({
  type: Yup.string().required('Cannot be blank.'),
  first_name: Yup.string().required('Cannot be blank.'),
  last_name: Yup.string().required('Cannot be blank.'),
  timezone: Yup.string().required('Cannot be blank.'),
  email: Yup.string().nullable().email('Must be valid email'),
  currency: Yup.number().required('Cannot be blank'),
  country: Yup.number().required('Cannot be blank'),
  services: Yup.string().required('Cannot be blank'),
  rush_percent: Yup.number().required('Cannot be blank').typeError('Must be a number')
})

const workingHours = [
  { key: 'mon', name: 'Monday', ranges: [] },
  { key: 'tue', name: 'Tuesday', ranges: [] },
  { key: 'wed', name: 'Wednesday', ranges: [] },
  { key: 'thu', name: 'Thursday', ranges: [] },
  { key: 'fri', name: 'Friday', ranges: [] },
  { key: 'sat', name: 'Saturday', ranges: [] },
  { key: 'sun', name: 'Sunday', ranges: [] }
]

const Formulario = () => {
  const { id } = useParams()
  const { data, isLoading, isError } = useFetch(id)
  const { data: countriesRaw } = useCacheCountry()
  const update = usePutForm(id)
  const history = useHistory()

  const countries = getCountriesForSelect(countriesRaw)

  function handleSubmit (values, bag) {
    values.prices.forEach((e) => delete e.id)
    const form = { ...values }

    update.mutate(form, {
      onSuccess: (data) => {
        bag.setSubmitting(false)
        onPressBack()
      },
      onError: (error, variables, context) => {
        bag.setSubmitting(false)
        if (error.response.status === 400) {
          const data = error.response?.data
          if (isNotValid(data)) {
            bag.setErrors(translateSymfonyErrorToFormik(data?.errors?.children))
          }
        }
      }
    })
  }

  const onPressBack = () => history.goBack()

  if (isLoading) {
    return (<Loader loading={isLoading} />)
  }

  if (isError) {
    return (<>Error</>)
  }

  const getInitialPrices = (prices) => {
    return (prices || []).map((p) => ({
      id: v4(),
      service: p.service,
      category: p.category,
      language: p.language,
      language_pair: p.language_pair,
      language_family: p.language_family,
      unit: p.unit,
      amount: p.amount
    }))
  }

  const country = data.vendor && data.vendor.country ? data.vendor.country.id : ''
  const currency = data.vendor && data.vendor.currency ? data.vendor.currency.id : ''
  const timezone = data.vendor ? data.vendor.timezone : ''

  const initialValues = {
    type: data.vendor.type,
    name: data.vendor.name,
    country,
    currency,
    timezone,
    website: data.vendor.website,
    notes: data.vendor.notes,
    first_name: data.vendor.first_name,
    last_name: data.vendor.last_name,
    phone: data.vendor.phone,
    email: data.vendor.email,
    skype: data.vendor.skype,
    years_of_experience: data.vendor.years_of_experience,
    work_with_mt: data.vendor.work_with_mt,
    work_on_holidays: data.vendor.work_on_holidays,
    languages: data.vendor.languages || [],
    language_pairs: data.vendor.language_pairs || [],
    work_days_grid: data.vendor.work_days_grid,
    ready_to_work: data.vendor.ready_to_work,
    specializations: data.vendor.specializations || [],
    categories: data.vendor.categories || [],
    services: data.vendor.services || [],
    prices: getInitialPrices(data.vendor.prices),
    rush_percent: data.vendor.rush_percent,
    //can_log_in: data.vendor.user ? data.vendor.user.active : false,
    can_log_in: data.vendor.can_log_in,
    prices_updated: false
  }

  const newUser = !data.vendor.user

  return (
    <BasicForm
      onPressBack={onPressBack}
      titulo='EDIT VENDOR'
      initialValues={initialValues}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
      render={({ isSubmitting, setFieldValue, touched, errors, values }) => (
        <>
          <Divider orientation='left' plain>
            General Fields
          </Divider>

          <Form.Item label='Type' name='type' hasFeedback showValidateSuccess required>
            <Select placeholder='Type' name='type' showSearch>
              <Select.Option value='' />
              <Select.Option value='Company'>Company</Select.Option>
              <Select.Option value='Freelancer'>Freelancer</Select.Option>
              <Select.Option value='In-House'>In-House</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label='Company Name'
            name='name'
            hasFeedback
            showValidateSuccess
            required={values.type === 'Company'}
          >
            <Input
              name='name'
              placeholder='Company Name'
              disabled={values.type !== 'Company'}
            />
          </Form.Item>

          <Form.Item label='Website' name='website'>
            <Input name='website' placeholder='Website' />
          </Form.Item>

          <Form.Item
            label='Time Zone'
            name='timezone'
            hasFeedback
            showValidateSuccess
            required
          >
            <Select
              placeholder='Time Zone'
              name='timezone'
              showSearch
              options={selectTimezones()}
              filterOption={_filter}
            />
          </Form.Item>

          <Form.Item
            label='Country'
            name='country'
            hasFeedback
            showValidateSuccess
            required
          >
            <Select
              placeholder='Country'
              name='country'
              showSearch
              options={countries}
              filterOption={_filter}
            />
          </Form.Item>

          <Form.Item label='Notes' name='notes'>
            <Input.TextArea name='notes' placeholder='Notes' />
          </Form.Item>

          <Divider orientation='left' plain>
            Primary Contact
          </Divider>
          <Form.Item
            label='First Name'
            name='first_name'
            hasFeedback
            showValidateSuccess
            required
          >
            <Input name='first_name' placeholder='First Name' />
          </Form.Item>
          <Form.Item
            label='Last Name'
            name='last_name'
            hasFeedback
            showValidateSuccess
            required
          >
            <Input name='last_name' placeholder='Last Name' />
          </Form.Item>
          <Form.Item label='Phone' name='phone'>
            <Input name='phone' placeholder='Phone' />
          </Form.Item>
          <Form.Item label='Email' name='email'>
            <Input name='email' placeholder='Email' />
          </Form.Item>
          <Form.Item label='Skype' name='skype'>
            <Input name='skype' placeholder='Skype' />
          </Form.Item>
          <Form.Item
            label='Can log in'
            name='can_log_in'
            hasFeedback
            extra={newUser && 'If checked, the system will send an invitation email.'}
          >
            <Checkbox name='can_log_in' />
          </Form.Item>

          <Divider orientation='left' plain>
            Working Hours
          </Divider>

          <Form.Item label='Ready to Work' name='ready_to_work'>
            <Checkbox name='ready_to_work' placeholder='Ready to Work' />
          </Form.Item>

          <Form.Item label='Work with MT' name='work_with_mt'>
            <Checkbox name='work_with_mt' placeholder='Work with MT' />
          </Form.Item>

          <Form.Item label='Work on Holidays' name='work_on_holidays'>
            <Checkbox name='work_on_holidays' placeholder='Work on Holidays' />
          </Form.Item>

          <Form.Item name='work_days' hidden />

          <FormAntd.Item
            label='Work Days'
            validateStatus={errors.work_days_grid ? 'error' : false}
            help={errors.work_days_grid ? errors.work_days_grid : false}
          >
            <WorkingHours
              data={values.work_days_grid || workingHours}
              onChange={(data) => setFieldValue('work_days_grid', data)}
            />
          </FormAntd.Item>

          <Divider orientation='left' plain>
            Services
          </Divider>

          <FormServices formik={{ isSubmitting, touched, setFieldValue, values, errors }} />

          <Form.Item label='Years of Experience' name='years_of_experience'>
            <Select placeholder='Years of Experience' name='years_of_experience' showSearch>
              <Select.Option value='' />
              <Select.Option value='1-3 years'>1-3 years</Select.Option>
              <Select.Option value='3-5 years'>3-5 years</Select.Option>
              <Select.Option value='5-10 years'>5-10 years</Select.Option>
              <Select.Option value='10+ years'>10+ years</Select.Option>
            </Select>
          </Form.Item>

          <FormPrices
            strictValues
            services={values.services}
            formik={{ isSubmitting, touched, setFieldValue, values, errors }}
          />

          <Divider plain />
        </>
      )}
    />
  )
}

export default Formulario
